import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, RefreshCw, Loader2, User, Pencil, X, Check } from "lucide-react";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Pagination } from "@/components/Pagination";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  "Solution Out of Scope": { border: "border-l-[#FF6B6B]", badge: "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800", dot: "bg-red-500" },
  "Solution In Scope": { border: "border-l-[#F5B731]", badge: "bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800", dot: "bg-amber-500" },
  "Solution Completed": { border: "border-l-[#39D98A]", badge: "bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800", dot: "bg-green-500" },
};

const statusCycle = { "Solution Out of Scope": "Solution In Scope", "Solution In Scope": "Solution Completed", "Solution Completed": "Solution Out of Scope" };

export const PainPointsPanel = () => {
  const { selectedProduct } = useAuth();
  const productId = selectedProduct?.id;
  const [points, setPoints] = useState([]);
  const [ppPage, setPpPage] = useState(1);
  const PP_PAGE_SIZE = 10;
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ title: "", description: "", stakeholder_name: "" });
  const [submitting, setSubmitting] = useState(false);
  const [stakeholders, setStakeholders] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchPoints = useCallback(async () => {
    try {
      const params = productId ? { product_id: productId } : {};
      const [res, sRes] = await Promise.all([
        axios.get(`${API}/painpoints`, { params }),
        axios.get(`${API}/stakeholders`, { params }),
      ]);
      setPoints(res.data);
      setStakeholders(sRes.data.filter(s => s.type === "business" || s.type === "Business"));
    } catch (err) {
      console.error("Failed to fetch pain points:", err);
    }
    setLoading(false);
  }, [productId]);

  useEffect(() => {
    fetchPoints();
  }, [fetchPoints]);

  const cycleStatus = async (point) => {
    const next = statusCycle[point.status];
    try {
      await axios.patch(`${API}/painpoints/${point.id}`, { status: next });
      setPoints((prev) => prev.map((p) => (p.id === point.id ? { ...p, status: next } : p)));
    } catch (err) {
      console.error("Failed to update status:", err);
    }
  };

  const addPoint = async () => {
    if (!formData.title.trim() || !formData.description.trim()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/painpoints`, { ...formData, product_id: productId });
      setPoints((prev) => [...prev, res.data]);
      setFormData({ title: "", description: "", stakeholder_name: "" });
      setShowForm(false);
    } catch (err) {
      console.error("Failed to add pain point:", err);
    }
    setSubmitting(false);
  };

  const deletePoint = async (id) => {
    try {
      await axios.delete(`${API}/painpoints/${id}`);
      setPoints((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      console.error("Failed to delete:", err);
    }
  };

  const updateStakeholder = async (pointId, name) => {
    try {
      await axios.patch(`${API}/painpoints/${pointId}`, { stakeholder_name: name });
      setPoints((prev) => prev.map((p) => (p.id === pointId ? { ...p, stakeholder_name: name } : p)));
    } catch (err) {
      console.error("Failed to update stakeholder:", err);
    }
  };

  const startEdit = (point) => {
    setEditingId(point.id);
    setEditData({ title: point.title, description: point.description, stakeholder_name: point.stakeholder_name || "" });
  };

  const saveEdit = async (pointId) => {
    try {
      await axios.patch(`${API}/painpoints/${pointId}`, editData);
      setPoints((prev) => prev.map((p) => (p.id === pointId ? { ...p, ...editData } : p)));
      setEditingId(null);
      setEditData({});
    } catch (err) {
      console.error("Failed to save edit:", err);
    }
  };

  const counts = {
    total: points.length,
    "Solution Out of Scope": points.filter((p) => p.status === "Solution Out of Scope").length,
    "Solution In Scope": points.filter((p) => p.status === "Solution In Scope").length,
    "Solution Completed": points.filter((p) => p.status === "Solution Completed").length,
  };

  return (
    <>
    <div data-testid="painpoints-panel">
      <p className="text-xs font-semibold tracking-widest uppercase text-[#FF6B6B] mb-3">
        Current Challenges
      </p>
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 dark:text-slate-100 font-['Outfit']">
        Pain Points
      </h1>
      <p className="text-base text-slate-500 dark:text-slate-400 mt-2">
        Key friction areas in the current manual review workflow
      </p>

      <Separator className="my-8 bg-slate-200 dark:bg-slate-700" />

      {/* Status counters */}
      <div className="flex items-center gap-3 mb-6 flex-wrap" data-testid="painpoint-counters">
        <Badge className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
          {counts.total} Total
        </Badge>
        {["Solution Out of Scope", "Solution In Scope", "Solution Completed"].map((s) => {
          const cfg = statusConfig[s];
          return (
            <Badge key={s} className={`${cfg.badge} border hover:opacity-90`}>
              <span className={`w-2 h-2 rounded-full ${cfg.dot} mr-1.5 inline-block`} />
              {counts[s]} {s}
            </Badge>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="pain-points-grid">
          {points.slice((ppPage - 1) * PP_PAGE_SIZE, ppPage * PP_PAGE_SIZE).map((point, i) => {
            const cfg = statusConfig[point.status] || statusConfig["Solution Out of Scope"];
            const isEditing = editingId === point.id;

            return (
              <Card
                key={point.id}
                className="card-modern overflow-hidden group"
                data-testid={`pain-point-card-${point.id}`}
              >
                {/* Status color top bar */}
                <div className={`h-1 ${point.status === "Solution Out of Scope" ? "bg-red-500" : point.status === "Solution In Scope" ? "bg-amber-500" : "bg-green-500"}`} />
                <CardContent className="p-5">
                  {isEditing ? (
                    /* ── Edit Mode ── */
                    <div className="space-y-3" data-testid={`painpoint-edit-form-${point.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Editing</span>
                        <button onClick={() => { setEditingId(null); setEditData({}); }} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <Input value={editData.title} onChange={(e) => setEditData(p => ({ ...p, title: e.target.value }))}
                        placeholder="Title" className="dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100 font-semibold" data-testid={`painpoint-edit-title-${point.id}`} />
                      <Textarea value={editData.description} onChange={(e) => setEditData(p => ({ ...p, description: e.target.value }))}
                        placeholder="Description" rows={3} className="dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100 text-sm" data-testid={`painpoint-edit-desc-${point.id}`} />
                      <div>
                        <label className="text-[10px] font-medium text-slate-500 dark:text-slate-400 mb-1 block uppercase tracking-wider">Business Stakeholder</label>
                        <Select value={editData.stakeholder_name || ""} onValueChange={(val) => setEditData(p => ({ ...p, stakeholder_name: val }))}>
                          <SelectTrigger className="dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100 text-sm" data-testid={`painpoint-edit-stakeholder-${point.id}`}>
                            <SelectValue placeholder="Select stakeholder" />
                          </SelectTrigger>
                          <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                            {stakeholders.map((s) => (
                              <SelectItem key={s.id} value={s.name} className="dark:text-slate-100 dark:hover:bg-slate-700">{s.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex gap-2 pt-1">
                        <Button onClick={() => saveEdit(point.id)} size="sm" className="bg-[#00D4C8] hover:bg-[#00B8AE] text-white" data-testid={`painpoint-edit-save-${point.id}`}>
                          <Check className="w-3.5 h-3.5 mr-1" /> Save
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => { setEditingId(null); setEditData({}); }} className="dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700">Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    /* ── View Mode ── */
                    <>
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex items-start gap-3">
                          <span className="flex-shrink-0 w-auto min-w-[28px] h-7 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 flex items-center justify-center text-[10px] font-bold font-['Outfit'] mt-0.5 px-2">
                            PP-{String(point.pp_number || ((ppPage - 1) * PP_PAGE_SIZE + i + 1)).padStart(3, '0')}
                          </span>
                          <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 font-['Outfit'] leading-snug">
                            {point.title}
                          </h3>
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => startEdit(point)}
                            className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all"
                            title="Edit" data-testid={`painpoint-edit-btn-${point.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => setDeleteTarget({ id: point.id, label: point.title })}
                            className="p-1.5 rounded-md text-slate-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-all"
                            title="Delete" data-testid={`painpoint-delete-${point.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>

                      <p className="text-sm text-slate-500 dark:text-slate-300 leading-relaxed mb-4 ml-10">
                        {point.description}
                      </p>

                      <div className="flex items-center justify-between ml-10">
                        <div className="flex items-center gap-3">
                          {/* Stakeholder badge */}
                          {point.stakeholder_name ? (
                            <div className="flex items-center gap-1.5 bg-slate-100 dark:bg-slate-700/60 rounded-full px-2.5 py-1">
                              <User className="w-3 h-3 text-slate-400 dark:text-slate-500" />
                              <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{point.stakeholder_name}</span>
                            </div>
                          ) : (
                            <Select value="" onValueChange={(val) => updateStakeholder(point.id, val)}>
                              <SelectTrigger className="h-7 text-xs w-40 border-dashed dark:bg-transparent dark:border-slate-600 dark:text-slate-400" data-testid={`painpoint-stakeholder-${point.id}`}>
                                <SelectValue placeholder="+ Assign owner" />
                              </SelectTrigger>
                              <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                                {stakeholders.map((s) => (
                                  <SelectItem key={s.id} value={s.name} className="dark:text-slate-100 dark:hover:bg-slate-700">{s.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>

                        {/* Status pill */}
                        <button
                          onClick={() => cycleStatus(point)}
                          className={`px-3 py-1 rounded-full text-[11px] font-semibold ${cfg.badge} border flex items-center gap-1.5 hover:opacity-80 transition-opacity`}
                          data-testid={`painpoint-status-${point.id}`}
                          title="Click to cycle status"
                        >
                          <RefreshCw className="w-3 h-3" />
                          {point.status}
                        </button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      {!loading && points.length > 0 && (
        <Pagination currentPage={ppPage} totalItems={points.length} pageSize={PP_PAGE_SIZE} onPageChange={setPpPage} />
      )}

      {/* Add pain point */}
      {showForm ? (
        <Card className="mt-6 card-modern" data-testid="add-painpoint-form">
          <CardContent className="p-6">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 font-['Outfit'] mb-4">Add Pain Point</h3>
            <div className="space-y-3">
              <Input
                placeholder="Pain point title"
                value={formData.title}
                onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))}
                className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
                data-testid="painpoint-title-input"
              />
              <Textarea
                placeholder="Describe the pain point..."
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
                data-testid="painpoint-description-input"
              />
              <div>
                <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block">Business Stakeholder</label>
                <Select value={formData.stakeholder_name} onValueChange={(val) => setFormData((p) => ({ ...p, stakeholder_name: val }))}>
                  <SelectTrigger className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100" data-testid="painpoint-stakeholder-input">
                    <SelectValue placeholder="Select stakeholder (optional)" />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                    {stakeholders.map((s) => (
                      <SelectItem key={s.id} value={s.name} className="dark:text-slate-100 dark:hover:bg-slate-700">{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={addPoint}
                  disabled={submitting || !formData.title.trim() || !formData.description.trim()}
                  className="bg-slate-700 hover:bg-slate-600 text-white"
                  data-testid="painpoint-submit-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {submitting ? "Adding..." : "Add Pain Point"}
                </Button>
                <Button variant="outline" onClick={() => { setShowForm(false); setFormData({ title: "", description: "", stakeholder_name: "" }); }} className="dark:bg-slate-800 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700">
                  Cancel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Button
          onClick={() => setShowForm(true)}
          className="mt-6 bg-slate-700 hover:bg-slate-600 text-white shadow-sm"
          data-testid="add-painpoint-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Pain Point
        </Button>
      )}
    </div>
    <ConfirmDeleteDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={() => deletePoint(deleteTarget?.id)} title="Delete Pain Point" description={`Are you sure you want to delete "${deleteTarget?.label}"? This action cannot be undone.`} />
    </>
  );
};